import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  ClipboardCheckIcon,
  CopyIcon,
  FlagIcon,
  GaugeIcon,
  InfoIcon,
  Loader2Icon,
  MedalIcon,
  PlusIcon,
  SaveIcon,
  SearchIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TargetIcon,
  Trash2Icon,
  TrophyIcon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../hooks/useAuth";
import { getTournamentTheme } from "../styles/themes";
import { fetchGolfCourses } from "../services/golfCourses";

const MINI_GAME_PRESETS = [
  {
    gameType: "closest_to_pin",
    name: "Closest to pin (CTP)",
    description: "Närmast hål på valt eller valda hål. Lägst distans vinner.",
  },
  {
    gameType: "longest_drive",
    name: "Longest drive",
    description: "Längsta godkända drive på valt eller valda hål. Högst distans vinner.",
  },
];

const ROUND_INFO_PRESETS = {
  Slagspel: `<strong>Tider</strong>\nTee time: \nSamling: \n\n<strong>Spelform</strong>\nIndividuellt slagspel. Lägst antal slag vinner.\n\n<strong>Övrigt</strong>\n`,
  "Handicap-justerat slagspel": `<strong>Tider</strong>\nTee time: \nSamling: \n\n<strong>Spelform</strong>\nIndividuellt slagspel där spelarens HCP dras av från bruttoresultatet.\n\n<strong>Övrigt</strong>\n`,
  "Scramble (2 man)": `<strong>Tider</strong>\nTee time: \nSamling: \n\n<strong>Spelform</strong>\nTvåmannascramble. Båda slår och laget väljer vilket läge som spelas vidare.\n\n<strong>Övrigt</strong>\n`,
  Bästboll: `<strong>Tider</strong>\nTee time: \nSamling: \n\n<strong>Spelform</strong>\nBästboll enligt turneringens upplägg.\n\n<strong>Övrigt</strong>\n`,
};

const KNOWN_PRESET_INFO = new Set(Object.values(ROUND_INFO_PRESETS));

const getMiniGameTypeLabel = (gameType) => {
  if (gameType === "closest_to_pin") return "CTP";
  if (gameType === "longest_drive") return "Longest drive";
  return "Egen sidotävling";
};

const formatHandicap = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "–";
  return Number.isInteger(number) ? String(number) : number.toFixed(1);
};

const defaultRound = (data = {}, index = 0) => ({
  id: data.id ?? null,
  name: data.name || "",
  courseId: data.courseId ?? data.course_id ?? null,
  modes: Array.isArray(data.modes) && data.modes.length > 0 ? [...data.modes] : [""],
  scrambleOptions: data.scrambleOptions
    ? {
        ...data.scrambleOptions,
        customTeams: { ...(data.scrambleOptions.customTeams || {}) },
      }
    : null,
  info: data.info || "",
  holes: Number(data.holes) === 9 ? 9 : 18,
});

const safeInfoHtml = (value = "") => {
  const escaped = String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

  return escaped
    .replace(/&lt;strong&gt;/gi, "<strong>")
    .replace(/&lt;\/strong&gt;/gi, "</strong>")
    .replace(/\n/g, "<br />");
};

const uniquePairsFromMap = (pairMap = {}) => {
  const seen = new Set();
  const pairs = [];

  Object.entries(pairMap).forEach(([playerOne, playerTwo]) => {
    const first = String(playerOne);
    const second = String(playerTwo || "");
    if (!first || !second || first === second) return;
    if (seen.has(first) || seen.has(second)) return;

    seen.add(first);
    seen.add(second);
    pairs.push([first, second]);
  });

  return pairs;
};


const sortPlayersByHandicap = (players = []) =>
  [...players].sort((first, second) => {
    const firstHandicap = Number(first.handicap);
    const secondHandicap = Number(second.handicap);

    if (firstHandicap !== secondHandicap) {
      return firstHandicap - secondHandicap;
    }

    return String(first.name || "").localeCompare(
      String(second.name || ""),
      "sv-SE"
    );
  });

const createBalancedScramblePairs = (players = []) => {
  const sorted = sortPlayersByHandicap(
    players.filter((player) => Number.isFinite(Number(player.handicap)))
  );
  const pairs = [];
  let left = 0;
  let right = sorted.length - 1;

  // För tvåmannalag ger låg + hög, näst låg + näst hög osv.
  // Det jämnar ut lagens sammanlagda HCP och är samtidigt lätt att förstå.
  while (left < right) {
    const lowPlayer = sorted[left];
    const highPlayer = sorted[right];

    pairs.push({
      players: [lowPlayer, highPlayer],
      combinedHandicap:
        Number(lowPlayer.handicap) + Number(highPlayer.handicap),
    });

    left += 1;
    right -= 1;
  }

  return {
    pairs,
    singlePlayer: left === right ? sorted[left] : null,
  };
};

const createOptimallyBalancedMainTeams = (players = []) => {
  const normalizedPlayers = players.map((player, index) => {
    const handicap = Number(player.handicap);

    if (!Number.isFinite(handicap)) {
      throw new Error(`${player.name || `Spelare ${index + 1}`} saknar ett giltigt HCP.`);
    }

    return {
      player,
      // Två decimaler räcker för att jämföra HCP-summor utan flyttalsproblem.
      scaledHandicap: Math.round(handicap * 100),
      index,
    };
  });

  if (normalizedPlayers.length < 2) {
    return {};
  }

  const smallerTeamSize = Math.floor(normalizedPlayers.length / 2);
  const totalHandicap = normalizedPlayers.reduce(
    (sum, item) => sum + item.scaledHandicap,
    0
  );

  // DP med exakt antal spelare. Varje möjlig HCP-summa sparar en kombination.
  // Därmed hittas den fördelning som ger minsta möjliga skillnad mellan lagen,
  // samtidigt som antalet spelare skiljer högst en.
  const combinationsByCount = Array.from(
    { length: smallerTeamSize + 1 },
    () => new Map()
  );
  combinationsByCount[0].set(0, []);

  normalizedPlayers.forEach((item) => {
    for (let count = smallerTeamSize; count >= 1; count -= 1) {
      const previousCombinations = combinationsByCount[count - 1];
      const currentCombinations = combinationsByCount[count];

      [...previousCombinations.entries()].forEach(([sum, indexes]) => {
        const nextSum = sum + item.scaledHandicap;

        if (!currentCombinations.has(nextSum)) {
          currentCombinations.set(nextSum, [...indexes, item.index]);
        }
      });
    }
  });

  let bestIndexes = [];
  let smallestDifference = Number.POSITIVE_INFINITY;

  combinationsByCount[smallerTeamSize].forEach((indexes, teamOneSum) => {
    const difference = Math.abs(totalHandicap - teamOneSum * 2);

    if (difference < smallestDifference) {
      smallestDifference = difference;
      bestIndexes = indexes;
    }
  });

  const teamOneIndexes = new Set(bestIndexes);

  return Object.fromEntries(
    normalizedPlayers.map((item) => [
      String(item.player.id),
      teamOneIndexes.has(item.index) ? "red" : "green",
    ])
  );
};

function SectionHeading({ number, icon: Icon, title, description, right }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#183d2d] text-sm font-bold text-white shadow-sm">
          {number}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-[#a88035]" aria-hidden="true" />}
            <h2 className="text-lg font-semibold tracking-[-0.015em] text-[#183d2d] sm:text-xl">
              {title}
            </h2>
          </div>
          {description && (
            <p className="mt-1 max-w-2xl text-sm leading-5 text-[#66716b]">
              {description}
            </p>
          )}
        </div>
      </div>
      {right}
    </div>
  );
}

function ChoiceCard({ active, disabled = false, title, description, icon: Icon, badge, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`relative min-h-[118px] rounded-2xl border p-4 text-left transition ${
        active
          ? "border-[#183d2d] bg-[#edf3ef] shadow-[0_0_0_1px_rgba(24,61,45,0.1)]"
          : "border-[#dce2de] bg-white hover:border-[#b8c6bd] hover:bg-[#fafcfb]"
      } ${disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${
            active ? "bg-[#183d2d] text-white" : "bg-[#f0f3f1] text-[#526159]"
          }`}
        >
          {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
        </span>
        {badge && (
          <span className="rounded-full bg-[#f3ead8] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-[#8a682a]">
            {badge}
          </span>
        )}
      </div>
      <p className="mt-3 text-sm font-semibold text-[#1f2d26]">{title}</p>
      <p className="mt-1 text-xs leading-5 text-[#69736e]">{description}</p>
      {active && (
        <CheckCircle2Icon className="absolute right-3 top-3 h-4 w-4 text-[#183d2d]" aria-hidden="true" />
      )}
    </button>
  );
}

function SegmentedButton({ active, onClick, children, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded-lg px-3 py-2 text-xs font-semibold transition sm:text-sm ${
        active
          ? "bg-[#183d2d] text-white shadow-sm"
          : "text-[#5f6a64] hover:bg-black/[0.035]"
      } disabled:cursor-not-allowed disabled:opacity-45`}
    >
      {children}
    </button>
  );
}

function CoursePicker({
  courses,
  loading = false,
  error = "",
  value = null,
  onChange,
}) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const selectedCourse = useMemo(
    () =>
      courses.find((course) => String(course.id) === String(value)) || null,
    [courses, value]
  );

  const filteredCourses = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("sv-SE");

    return courses
      .filter((course) => {
        if (!query) return true;

        const club = course.golf_clubs;
        return [course.name, club?.name, club?.city]
          .filter(Boolean)
          .some((item) =>
            String(item).toLocaleLowerCase("sv-SE").includes(query)
          );
      })
      .slice(0, 12);
  }, [courses, search]);

  const chooseCourse = (course) => {
    onChange(course);
    setSearch("");
    setIsOpen(false);
  };

  return (
    <div>
      <span className="mb-1.5 block text-[9px] font-semibold uppercase tracking-[0.15em] text-[#78817c]">
        Golfbana <span className="normal-case tracking-normal text-[#a1a8a4]">(valfritt)</span>
      </span>

      <div
        className="relative"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsOpen(false);
          }
        }}
      >
        <div
          className={`flex items-center gap-2 rounded-xl border bg-white px-3 transition ${
            isOpen
              ? "border-[#183d2d] ring-2 ring-[#183d2d]/10"
              : "border-[#ccd6d0]"
          }`}
        >
          <SearchIcon className="h-4 w-4 shrink-0 text-[#8b958f]" />

          <input
            type="text"
            value={search}
            onFocus={() => setIsOpen(true)}
            onChange={(event) => {
              setSearch(event.target.value);
              setIsOpen(true);
            }}
            placeholder={
              selectedCourse
                ? `${selectedCourse.golf_clubs?.name ? `${selectedCourse.golf_clubs.name} · ` : ""}${selectedCourse.name}`
                : "Sök bana, klubb eller ort…"
            }
            className="min-w-0 flex-1 bg-transparent py-2.5 text-sm font-medium text-[#26362e] outline-none placeholder:text-[#8e9892]"
          />

          {selectedCourse && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setSearch("");
                setIsOpen(false);
              }}
              className="shrink-0 rounded-lg p-1 text-[#8a948e] transition hover:bg-[#f0f3f1] hover:text-[#38463e]"
              title="Ta bort vald bana"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {selectedCourse && !search && (
          <div className="mt-2 flex items-center gap-2 rounded-xl border border-[#d8e2dc] bg-[#f5f8f6] px-3 py-2">
            <FlagIcon className="h-3.5 w-3.5 shrink-0 text-[#a88035]" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-[#26362e]">
                {selectedCourse.name}
              </p>
              <p className="truncate text-[10px] text-[#7b857f]">
                {[
                  selectedCourse.golf_clubs?.name,
                  selectedCourse.golf_clubs?.city,
                  `${selectedCourse.hole_count || 18} hål`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          </div>
        )}

        {isOpen && (
          <div className="absolute left-0 right-0 top-[46px] z-30 overflow-hidden rounded-xl border border-[#d8dfdb] bg-white shadow-[0_16px_40px_rgba(26,45,35,0.16)]">
            <div className="max-h-72 overflow-y-auto p-1.5">
              {loading ? (
                <div className="flex items-center justify-center gap-2 px-3 py-4 text-xs text-[#737d77]">
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                  Hämtar golfbanor…
                </div>
              ) : error ? (
                <p className="px-3 py-4 text-xs leading-5 text-[#9b4a47]">
                  {error}
                </p>
              ) : filteredCourses.length === 0 ? (
                <p className="px-3 py-4 text-xs text-[#7b857f]">
                  Ingen golfbana matchar sökningen.
                </p>
              ) : (
                filteredCourses.map((course) => {
                  const active =
                    selectedCourse &&
                    String(selectedCourse.id) === String(course.id);

                  return (
                    <button
                      key={course.id}
                      type="button"
                      onClick={() => chooseCourse(course)}
                      className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition ${
                        active
                          ? "bg-[#edf3ef]"
                          : "hover:bg-[#f6f8f6]"
                      }`}
                    >
                      <span
                        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                          active
                            ? "bg-[#183d2d] text-white"
                            : "bg-[#eef2ef] text-[#607068]"
                        }`}
                      >
                        <FlagIcon className="h-3.5 w-3.5" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-[#26362e]">
                          {course.name}
                        </span>
                        <span className="mt-0.5 block truncate text-[10px] text-[#7d8781]">
                          {[
                            course.golf_clubs?.name,
                            course.golf_clubs?.city,
                            `${course.hole_count || 18} hål`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>

                      <span className="mt-1 shrink-0 rounded-full bg-[#f3ead8] px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.08em] text-[#866527]">
                        {course.approval_status === "approved"
                          ? "Verifierad"
                          : "Egen bana"}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function NewTournament({
  editMode = false,
  tournamentData = null,
  setActiveTab = null,
  setTournament = null,
  onSaved = null,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = getTournamentTheme(tournamentData?.theme_key);

  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState("");
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [tournamentName, setTournamentName] = useState("");
  const [rounds, setRounds] = useState([defaultRound({}, 0)]);
  const [teamMode, setTeamMode] = useState(false);
  const [teamAssignments, setTeamAssignments] = useState({});
  const [teamCaptains, setTeamCaptains] = useState({ red: "", green: "" });
  const [miniGames, setMiniGames] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [newName, setNewName] = useState("");
  const [newHandicap, setNewHandicap] = useState("");
  const [newClub, setNewClub] = useState("");
  const [playerSearch, setPlayerSearch] = useState("");
  const [deletedRoundIds, setDeletedRoundIds] = useState([]);
  const [previewRoundIndexes, setPreviewRoundIndexes] = useState([]);
  const [openRoundIndexes, setOpenRoundIndexes] = useState([0]);
  const [scrambleInfoIndexes, setScrambleInfoIndexes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [formNotice, setFormNotice] = useState("");

  const selectedPlayers = useMemo(
    () =>
      selectedPlayerIds
        .map((id) => players.find((player) => String(player.id) === String(id)))
        .filter(Boolean),
    [players, selectedPlayerIds]
  );

  const getCourseById = (courseId) =>
    courses.find((course) => String(course.id) === String(courseId)) || null;

  const getRoundDisplayName = (round, index) =>
    round.name?.trim() ||
    getCourseById(round.courseId)?.name ||
    `Runda ${index + 1}`;


  const teamBalanceOverview = useMemo(() => {
    const summary = {
      red: { count: 0, handicap: 0 },
      green: { count: 0, handicap: 0 },
    };

    selectedPlayers.forEach((player) => {
      const teamKey = teamAssignments[String(player.id)];
      if (!summary[teamKey]) return;

      summary[teamKey].count += 1;
      summary[teamKey].handicap += Number(player.handicap) || 0;
    });

    return {
      ...summary,
      difference: Math.abs(summary.red.handicap - summary.green.handicap),
    };
  }, [selectedPlayers, teamAssignments]);

  const getScrambleHandicapPairGroups = (round) => {
    if (round.scrambleOptions?.pairing !== "handicap") return [];

    if (!teamMode) {
      return [
        {
          key: "all",
          label: "Alla spelare",
          ...createBalancedScramblePairs(selectedPlayers),
        },
      ];
    }

    const groups = [
      {
        key: "red",
        label: teamCaptains.red || "Lag 1",
        players: selectedPlayers.filter(
          (player) => teamAssignments[String(player.id)] === "red"
        ),
      },
      {
        key: "green",
        label: teamCaptains.green || "Lag 2",
        players: selectedPlayers.filter(
          (player) => teamAssignments[String(player.id)] === "green"
        ),
      },
    ].map((group) => ({
      key: group.key,
      label: group.label,
      ...createBalancedScramblePairs(group.players),
    }));

    const unassignedPlayers = selectedPlayers.filter(
      (player) => !["red", "green"].includes(teamAssignments[String(player.id)])
    );

    if (unassignedPlayers.length > 0) {
      groups.push({
        key: "unassigned",
        label: "Saknar huvudlag",
        ...createBalancedScramblePairs(unassignedPlayers),
      });
    }

    return groups;
  };

  const filteredPlayers = useMemo(() => {
    const query = playerSearch.trim().toLocaleLowerCase("sv-SE");
    return [...players]
      .sort((first, second) => {
        const firstHcp = Number(first.handicap);
        const secondHcp = Number(second.handicap);
        if (Number.isFinite(firstHcp) && Number.isFinite(secondHcp) && firstHcp !== secondHcp) {
          return firstHcp - secondHcp;
        }
        return String(first.name || "").localeCompare(String(second.name || ""), "sv-SE");
      })
      .filter((player) => {
        if (!query) return true;
        return [player.name, player.home_club, player.club, player.handicap]
          .filter((value) => value != null)
          .some((value) => String(value).toLocaleLowerCase("sv-SE").includes(query));
      });
  }, [playerSearch, players]);

  const hasHandicapRound = useMemo(
    () =>
      rounds.some((round) => {
        const mode = round.modes?.[0];
        return (
          mode === "Handicap-justerat slagspel" ||
          (mode === "Scramble (2 man)" &&
            (round.scrambleOptions?.scrambleWithHandicap ||
              round.scrambleOptions?.pairing === "handicap"))
        );
      }),
    [rounds]
  );

  const validationIssues = useMemo(() => {
    const issues = [];

    if (!tournamentName.trim()) {
      issues.push("Ge turneringen ett namn.");
    }

    if (selectedPlayerIds.length === 0) {
      issues.push("Välj minst en spelare.");
    }

    if (rounds.length === 0) {
      issues.push("Turneringen behöver minst en runda.");
    }

    rounds.forEach((round, index) => {
      const mode = round.modes?.[0];
      if (!mode) {
        issues.push(`Välj spelform för runda ${index + 1}.`);
      }

      if (mode === "Poängbogey") {
        issues.push(`Poängbogey är ännu inte aktiverat för runda ${index + 1}.`);
      }

      if (mode === "Scramble (2 man)" && round.scrambleOptions?.scrambleWithHandicap) {
        const low = Number(round.scrambleOptions.lowPct);
        const high = Number(round.scrambleOptions.highPct);
        if (
          !Number.isFinite(low) ||
          !Number.isFinite(high) ||
          low < 0 ||
          high < 0 ||
          low > 100 ||
          high > 100
        ) {
          issues.push(`Ange giltiga HCP-procenter för runda ${index + 1}.`);
        }
      }
    });

    if (hasHandicapRound) {
      const missingHandicap = selectedPlayers.find(
        (player) => !Number.isFinite(Number(player.handicap))
      );
      if (missingHandicap) {
        issues.push(`${missingHandicap.name} saknar ett giltigt HCP.`);
      }
    }

    if (teamMode) {
      const unassigned = selectedPlayerIds.filter(
        (playerId) => !["red", "green"].includes(teamAssignments[playerId])
      );
      if (unassigned.length > 0) {
        issues.push(`${unassigned.length} spelare saknar lag i den stora lagtävlingen.`);
      }

      const redCount = selectedPlayerIds.filter(
        (playerId) => teamAssignments[playerId] === "red"
      ).length;
      const greenCount = selectedPlayerIds.filter(
        (playerId) => teamAssignments[playerId] === "green"
      ).length;
      if (selectedPlayerIds.length > 0 && (redCount === 0 || greenCount === 0)) {
        issues.push("Båda lagen behöver minst en spelare.");
      }
    }

    const validMiniGames = miniGames.filter((game) => game.name.trim());
    const normalizedNames = validMiniGames.map((game) =>
      game.name.trim().toLocaleLowerCase("sv-SE")
    );
    if (new Set(normalizedNames).size !== normalizedNames.length) {
      issues.push("Sidotävlingar måste ha unika namn.");
    }

    const invalidDeduction = validMiniGames.find(
      (game) => game.subtract && !(Number(game.deduction) > 0)
    );
    if (invalidDeduction) {
      issues.push(`Ange ett slagavdrag större än 0 för ${invalidDeduction.name}.`);
    }

    return issues;
  }, [
    hasHandicapRound,
    miniGames,
    rounds,
    selectedPlayerIds,
    selectedPlayers,
    teamAssignments,
    teamMode,
    tournamentName,
  ]);

  const advisoryItems = useMemo(() => {
    const items = [];

    rounds.forEach((round, index) => {
      if (round.modes?.[0] === "Scramble (2 man)" && selectedPlayerIds.length % 2 !== 0) {
        items.push(`Runda ${index + 1}: udda antal spelare innebär att någon kan bli ensam.`);
      }

      if (
        round.modes?.[0] === "Scramble (2 man)" &&
        round.scrambleOptions?.pairing === "custom"
      ) {
        const pairedIds = new Set(
          uniquePairsFromMap(round.scrambleOptions.customTeams).flat()
        );
        const missing = selectedPlayerIds.filter(
          (playerId) => !pairedIds.has(String(playerId))
        );
        if (missing.length > 0) {
          items.push(`Runda ${index + 1}: ${missing.length} spelare saknar eget scramble-par.`);
        }
      }
    });

    if (miniGames.length === 0) {
      items.push("Inga sidotävlingar är tillagda.");
    }

    return [...new Set(items)];
  }, [miniGames.length, rounds, selectedPlayerIds]);

  useEffect(() => {
    let cancelled = false;

    const fetchPlayers = async () => {
      if (!user?.id) {
        setPlayersLoading(false);
        return;
      }

      setPlayersLoading(true);
      const { data, error } = await supabase
        .from("players")
        .select("*")
        .eq("user_id", user.id);

      if (cancelled) return;

      if (error) {
        console.error("Kunde inte läsa spelare:", error);
        setFormError("Spelarna kunde inte hämtas. Uppdatera sidan och försök igen.");
      } else {
        setPlayers(data || []);
      }
      setPlayersLoading(false);
    };

    fetchPlayers();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadCourses = async () => {
      if (!user?.id) {
        setCourses([]);
        setCoursesLoading(false);
        return;
      }

      setCoursesLoading(true);
      setCoursesError("");

      try {
        const data = await fetchGolfCourses();
        if (!cancelled) {
          setCourses(data || []);
        }
      } catch (error) {
        console.error("Kunde inte läsa golfbanor:", error);
        if (!cancelled) {
          setCourses([]);
          setCoursesError(
            "Golfbanorna kunde inte hämtas. Du kan fortfarande skapa rundan utan bana."
          );
        }
      } finally {
        if (!cancelled) {
          setCoursesLoading(false);
        }
      }
    };

    loadCourses();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!editMode || !tournamentData) return;

    setTournamentName(tournamentData.name || "");
    setSelectedPlayerIds((tournamentData.players || []).map(String));
    setTeamMode(Boolean(tournamentData.team_mode));
    setTeamCaptains(
      tournamentData.team_data?.captains || { red: "", green: "" }
    );
    setTeamAssignments(tournamentData.team_data?.assignments || {});
    setMiniGames(
      (tournamentData.miniGames || []).map((game) => ({
        id: game.id,
        name: game.name || "",
        gameType: game.game_type || "custom",
        subtract: Boolean(game.subtract_from_score),
        deduction: Number(game.deduction_value) || 0,
      }))
    );

    const loadedRounds = (tournamentData.rounds || []).map((round, index) =>
      defaultRound(round, index)
    );
    setRounds(loadedRounds.length > 0 ? loadedRounds : [defaultRound({}, 0)]);
    setOpenRoundIndexes(loadedRounds.map((_, index) => index));
  }, [editMode, tournamentData]);

  const clearMessages = () => {
    setFormError("");
    setFormNotice("");
  };

  const updateRound = (index, updater) => {
    setRounds((current) =>
      current.map((round, roundIndex) =>
        roundIndex === index
          ? typeof updater === "function"
            ? updater(round)
            : { ...round, ...updater }
          : round
      )
    );
  };

  const togglePlayer = (playerId) => {
    clearMessages();
    const id = String(playerId);

    setSelectedPlayerIds((current) => {
      const selected = current.includes(id);
      return selected ? current.filter((item) => item !== id) : [...current, id];
    });

    if (selectedPlayerIds.includes(id)) {
      setTeamAssignments((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });

      setRounds((current) =>
        current.map((round) => {
          const customTeams = round.scrambleOptions?.customTeams;
          if (!customTeams || !customTeams[id]) return round;

          const partner = String(customTeams[id]);
          const nextTeams = { ...customTeams };
          delete nextTeams[id];
          delete nextTeams[partner];

          return {
            ...round,
            scrambleOptions: {
              ...round.scrambleOptions,
              customTeams: nextTeams,
            },
          };
        })
      );
    }
  };

  const selectAllFilteredPlayers = () => {
    clearMessages();
    const filteredIds = filteredPlayers.map((player) => String(player.id));
    setSelectedPlayerIds((current) => [...new Set([...current, ...filteredIds])]);
  };

  const clearSelectedPlayers = () => {
    clearMessages();
    setSelectedPlayerIds([]);
    setTeamAssignments({});
    setRounds((current) =>
      current.map((round) =>
        round.scrambleOptions
          ? {
              ...round,
              scrambleOptions: {
                ...round.scrambleOptions,
                customTeams: {},
              },
            }
          : round
      )
    );
  };

  const assignTeam = (playerId, teamKey) => {
    setTeamAssignments((current) => ({
      ...current,
      [String(playerId)]: teamKey,
    }));
  };

  const autoBalanceTeams = () => {
    try {
      const nextAssignments = createOptimallyBalancedMainTeams(selectedPlayers);
      const totals = { red: 0, green: 0 };
      const counts = { red: 0, green: 0 };

      selectedPlayers.forEach((player) => {
        const teamKey = nextAssignments[String(player.id)];
        if (!teamKey) return;

        totals[teamKey] += Number(player.handicap);
        counts[teamKey] += 1;
      });

      setTeamAssignments(nextAssignments);
      setFormNotice(
        `Jämnaste fördelningen hittad: ${counts.red} spelare / HCP ${formatHandicap(
          totals.red
        )} och ${counts.green} spelare / HCP ${formatHandicap(totals.green)}.`
      );
      setFormError("");
    } catch (error) {
      setFormError(error?.message || "Kunde inte balansera lagen efter HCP.");
      setFormNotice("");
    }
  };

  const handleRoundsChange = (rawValue) => {
    const parsed = Number.parseInt(rawValue, 10);
    if (!Number.isFinite(parsed)) return;

    const count = Math.min(12, Math.max(1, parsed));

    setRounds((current) => {
      // Om antal rundor minskas i edit-läge måste de borttagna DB-rundorna
      // också registreras för faktisk radering vid nästa sparning.
      if (count < current.length) {
        const removedIds = current
          .slice(count)
          .map((round) => round.id)
          .filter(Boolean);

        if (removedIds.length > 0) {
          setDeletedRoundIds((existing) => [
            ...new Set([...existing, ...removedIds]),
          ]);
        }
      }

      return Array.from({ length: count }, (_, index) =>
        current[index] ? current[index] : defaultRound({}, index)
      );
    });

    setOpenRoundIndexes((current) => [
      ...new Set(current.filter((index) => index < count).concat(count - 1)),
    ]);
  };

  const addRound = () => {
    const nextIndex = rounds.length;
    setRounds((current) => [...current, defaultRound({}, nextIndex)]);
    setOpenRoundIndexes((current) => [...new Set([...current, nextIndex])]);
  };

  const duplicateRound = (index) => {
    const source = rounds[index];
    const copy = defaultRound(
      {
        ...source,
        id: null,
        name: `${getRoundDisplayName(source, index)} – kopia`,
        scrambleOptions: source.scrambleOptions
          ? {
              ...source.scrambleOptions,
              customTeams: { ...(source.scrambleOptions.customTeams || {}) },
            }
          : null,
      },
      rounds.length
    );

    setRounds((current) => [...current, copy]);
    setOpenRoundIndexes((current) => [...new Set([...current, rounds.length])]);
  };

  const removeRound = (index) => {
    if (rounds.length <= 1) return;
    const roundToRemove = rounds[index];
    if (roundToRemove.id) {
      setDeletedRoundIds((current) => [...new Set([...current, roundToRemove.id])]);
    }

    setRounds((current) =>
      current
        .filter((_, roundIndex) => roundIndex !== index)
        .map((round, roundIndex) => ({
          ...round,
          name:
            round.name === `Runda ${roundIndex + 2}`
              ? `Runda ${roundIndex + 1}`
              : round.name,
        }))
    );
    setOpenRoundIndexes((current) =>
      current
        .filter((roundIndex) => roundIndex !== index)
        .map((roundIndex) => (roundIndex > index ? roundIndex - 1 : roundIndex))
    );
  };

  const selectRoundMode = (index, mode) => {
    clearMessages();
    updateRound(index, (round) => {
      const currentInfo = round.info || "";
      const shouldUsePreset = !currentInfo.trim() || KNOWN_PRESET_INFO.has(currentInfo);

      if (mode === "Scramble (2 man)") {
        return {
          ...round,
          modes: [mode],
          info: shouldUsePreset ? ROUND_INFO_PRESETS[mode] : currentInfo,
          scrambleOptions: {
            scrambleWithHandicap:
              round.scrambleOptions?.scrambleWithHandicap ?? false,
            pairing:
              round.scrambleOptions?.pairing || (index === 0 ? "handicap" : "position"),
            lowPct: round.scrambleOptions?.lowPct ?? 35,
            highPct: round.scrambleOptions?.highPct ?? 15,
            customTeams: { ...(round.scrambleOptions?.customTeams || {}) },
          },
        };
      }

      return {
        ...round,
        modes: [mode],
        info: shouldUsePreset ? ROUND_INFO_PRESETS[mode] || "" : currentInfo,
        scrambleOptions: null,
      };
    });
  };

  const updateScrambleOption = (index, key, value) => {
    if (key === "pairing" && value === "position" && index === 0) {
      setFormError(
        "Första rundan kan inte paras efter position eftersom det ännu saknas en ställning."
      );
      return;
    }

    setFormError("");
    updateRound(index, (round) => ({
      ...round,
      scrambleOptions: {
        ...(round.scrambleOptions || {}),
        [key]: value,
        ...(key === "pairing" && value === "custom"
          ? { customTeams: { ...(round.scrambleOptions?.customTeams || {}) } }
          : {}),
      },
    }));
  };

  const addCustomPair = (roundIndex, playerOne, playerTwo) => {
    const first = String(playerOne);
    const second = String(playerTwo);
    if (!first || !second || first === second) return;

    updateRound(roundIndex, (round) => ({
      ...round,
      scrambleOptions: {
        ...round.scrambleOptions,
        customTeams: {
          ...(round.scrambleOptions?.customTeams || {}),
          [first]: second,
          [second]: first,
        },
      },
    }));
  };

  const removeCustomPair = (roundIndex, playerOne, playerTwo) => {
    updateRound(roundIndex, (round) => {
      const nextTeams = { ...(round.scrambleOptions?.customTeams || {}) };
      delete nextTeams[String(playerOne)];
      delete nextTeams[String(playerTwo)];
      return {
        ...round,
        scrambleOptions: {
          ...round.scrambleOptions,
          customTeams: nextTeams,
        },
      };
    });
  };

  const toggleRoundPreview = (index) => {
    setPreviewRoundIndexes((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : [...current, index]
    );
  };

  const toggleRoundOpen = (index) => {
    setOpenRoundIndexes((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : [...current, index]
    );
  };

  const toggleScrambleInfo = (index) => {
    setScrambleInfoIndexes((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : [...current, index]
    );
  };

  const addMiniGamePreset = (preset) => {
    const alreadyExists = miniGames.some(
      (game) => game.gameType === preset.gameType
    );
    if (alreadyExists) {
      setFormNotice(`${preset.name} finns redan i turneringen.`);
      return;
    }

    setMiniGames((current) => [
      ...current,
      {
        id: null,
        name: preset.name,
        gameType: preset.gameType,
        subtract: false,
        deduction: 0,
      },
    ]);
    setFormNotice(`${preset.name} har lagts till.`);
    setFormError("");
  };

  const addCustomMiniGame = () => {
    setMiniGames((current) => [
      ...current,
      {
        id: null,
        name: "",
        gameType: "custom",
        subtract: false,
        deduction: 0,
      },
    ]);
  };

  const updateMiniGame = (index, patch) => {
    setMiniGames((current) =>
      current.map((game, gameIndex) =>
        gameIndex === index ? { ...game, ...patch } : game
      )
    );
  };

  const removeMiniGame = (index) => {
    setMiniGames((current) =>
      current.filter((_, gameIndex) => gameIndex !== index)
    );
  };

  const handleQuickAddPlayer = async () => {
    clearMessages();
    const handicap = Number.parseFloat(String(newHandicap).replace(",", "."));

    if (!newName.trim() || !Number.isFinite(handicap)) {
      setFormError("Fyll i spelarens namn och ett giltigt HCP.");
      return;
    }

    const newPlayer = {
      name: newName.trim(),
      handicap,
      home_club: newClub.trim(),
      user_id: user.id,
    };

    const { data, error } = await supabase
      .from("players")
      .insert([newPlayer])
      .select()
      .single();

    if (error) {
      console.error("Kunde inte skapa spelare:", error);
      setFormError("Spelaren kunde inte sparas.");
      return;
    }

    setPlayers((current) => [...current, data]);
    setSelectedPlayerIds((current) => [...new Set([...current, String(data.id)])]);
    setNewName("");
    setNewHandicap("");
    setNewClub("");
    setShowAddPlayer(false);
    setFormNotice(`${data.name} skapades och valdes till turneringen.`);
  };

  const rebuildTournamentData = async (tournamentId, playerIds) => {
    const { data: updatedTournament, error: tournamentError } = await supabase
      .from("tournaments")
      .select("*")
      .eq("id", tournamentId)
      .single();
    if (tournamentError || !updatedTournament) throw tournamentError;

    const { data: playersResponse, error: playersError } = await supabase
      .from("players")
      .select("*")
      .in("id", playerIds);
    if (playersError) throw playersError;

    const playerData = (playersResponse || []).map((player) => ({
      id: player.id,
      name: player.name,
      handicap: player.handicap,
      club: player.home_club,
      image: player.image_yrl || player.image_url || null,
      scores: [],
      miniGameScores: {},
    }));

    const { data: roundsData, error: roundsError } = await supabase
      .from("rounds")
      .select("*")
      .eq("tournament_id", tournamentId)
      .order("order_index", { ascending: true });
    if (roundsError) throw roundsError;

    const roundIds = (roundsData || []).map((round) => round.id);
    let scrambleSettings = [];
    let roundModes = [];
    let customPairs = [];

    if (roundIds.length > 0) {
      const [scrambleResponse, modesResponse, pairsResponse] = await Promise.all([
        supabase.from("scramble_settings").select("*").in("round_id", roundIds),
        supabase.from("round_modes").select("*").in("round_id", roundIds),
        supabase.from("scramble_custom_pairs").select("*").in("round_id", roundIds),
      ]);

      if (scrambleResponse.error) throw scrambleResponse.error;
      if (modesResponse.error) throw modesResponse.error;
      if (pairsResponse.error) throw pairsResponse.error;

      scrambleSettings = scrambleResponse.data || [];
      roundModes = modesResponse.data || [];
      customPairs = pairsResponse.data || [];
    }

    const rebuiltRounds = (roundsData || []).map((round) => {
      const scramble = scrambleSettings.find(
        (setting) => setting.round_id === round.id
      );
      const pairs = customPairs.filter((pair) => pair.round_id === round.id);
      const pairMap = {};
      pairs.forEach((pair) => {
        pairMap[pair.player_1_id] = pair.player_2_id;
        pairMap[pair.player_2_id] = pair.player_1_id;
      });

      return {
        ...round,
        modes: roundModes
          .filter((mode) => mode.round_id === round.id)
          .sort((first, second) => first.position - second.position)
          .map((mode) => mode.mode),
        scrambleOptions: scramble
          ? {
              scrambleWithHandicap: scramble.scramble_with_handicap,
              pairing: scramble.pairing,
              lowPct: scramble.low_hcp,
              highPct: scramble.high_hcp,
              customTeams: pairMap,
            }
          : null,
        teams:
          scramble?.pairing === "custom" ? uniquePairsFromMap(pairMap) : [],
      };
    });

    const { data: miniGamesData, error: miniGamesError } = await supabase
      .from("mini_games")
      .select("*")
      .eq("tournament_id", tournamentId);
    if (miniGamesError) throw miniGamesError;

    return {
      ...updatedTournament,
      players: playerIds,
      playerData,
      rounds: rebuiltRounds,
      miniGames: miniGamesData || [],
    };
  };

  const saveTournamentMetadata = async (tournamentId = null) => {
    const selectedAssignments = Object.fromEntries(
      selectedPlayerIds
        .filter((playerId) => teamAssignments[playerId])
        .map((playerId) => [playerId, teamAssignments[playerId]])
    );

    const payload = {
      name: tournamentName.trim(),
      user_id: user.id,
      team_mode: teamMode,
      team_data: teamMode
        ? {
            captains: {
              red: teamCaptains.red.trim(),
              green: teamCaptains.green.trim(),
            },
            assignments: selectedAssignments,
            colors: tournamentData?.team_data?.colors || {
              red: "#9F3A38",
              green: "#2F6B4F",
            },
          }
        : null,
    };

    if (!tournamentId) {
      const { data, error } = await supabase
        .from("tournaments")
        .insert([payload])
        .select()
        .single();
      if (error) throw error;
      return data.id;
    }

    const { error } = await supabase
      .from("tournaments")
      .update(payload)
      .eq("id", tournamentId);
    if (error) throw error;
    return tournamentId;
  };

  const syncTournamentPlayers = async (tournamentId) => {
    const { data: existingLinks, error: existingError } = await supabase
      .from("tournament_players")
      .select("player_id")
      .eq("tournament_id", tournamentId);
    if (existingError) throw existingError;

    const existingSet = new Set(
      (existingLinks || []).map((link) => String(link.player_id))
    );
    const selectedSet = new Set(selectedPlayerIds.map(String));

    // tournament_players är en ren kopplingstabell. Befintliga länkar ska inte
    // UPDATE:as vid varje redigering. Vi synkar endast faktiska skillnader.
    const removedIds = [...existingSet].filter(
      (playerId) => !selectedSet.has(playerId)
    );
    const addedIds = [...selectedSet].filter(
      (playerId) => !existingSet.has(playerId)
    );

    if (removedIds.length > 0) {
      const { error: removeError } = await supabase
        .from("tournament_players")
        .delete()
        .eq("tournament_id", tournamentId)
        .in("player_id", removedIds);
      if (removeError) throw removeError;
    }

    if (addedIds.length > 0) {
      const payload = addedIds.map((playerId) => ({
        tournament_id: tournamentId,
        player_id: playerId,
      }));

      const { error: insertError } = await supabase
        .from("tournament_players")
        .insert(payload);
      if (insertError) throw insertError;
    }
  };

  const saveMiniGames = async (tournamentId) => {
    const validMiniGames = miniGames
      .map((game) => ({
        id: game.id || null,
        name: game.name.trim(),
        game_type: game.gameType || "custom",
        subtract_from_score: Boolean(game.subtract),
        deduction_value: game.subtract ? Number(game.deduction) || 0 : 0,
      }))
      .filter((game) => game.name);

    const { data: existingGames, error: existingError } = await supabase
      .from("mini_games")
      .select("id")
      .eq("tournament_id", tournamentId);
    if (existingError) throw existingError;

    const retainedIds = new Set(
      validMiniGames.map((game) => game.id).filter(Boolean)
    );
    const removedIds = (existingGames || [])
      .filter((game) => !retainedIds.has(game.id))
      .map((game) => game.id);

    if (removedIds.length > 0) {
      const { error: scoreDeleteError } = await supabase
        .from("mini_game_scores")
        .delete()
        .in("mini_game_id", removedIds);
      if (scoreDeleteError) throw scoreDeleteError;

      const { error: gameDeleteError } = await supabase
        .from("mini_games")
        .delete()
        .in("id", removedIds);
      if (gameDeleteError) throw gameDeleteError;
    }

    for (const game of validMiniGames.filter((item) => item.id)) {
      const { error } = await supabase
        .from("mini_games")
        .update({
          name: game.name,
          game_type: game.game_type,
          subtract_from_score: game.subtract_from_score,
          deduction_value: game.deduction_value,
        })
        .eq("id", game.id)
        .eq("tournament_id", tournamentId);
      if (error) throw error;
    }

    const newGames = validMiniGames
      .filter((game) => !game.id)
      .map(({ id, ...game }) => ({ ...game, tournament_id: tournamentId }));

    if (newGames.length > 0) {
      const { error } = await supabase.from("mini_games").insert(newGames);
      if (error) throw error;
    }
  };

  const clearRemovedRounds = async () => {
    const removedIds = deletedRoundIds.filter(Boolean);
    if (removedIds.length === 0) return;

    const dependentTables = [
      "scores",
      "mini_game_scores",
      "scramble_custom_pairs",
      "scramble_settings",
      "round_modes",
    ];

    for (const table of dependentTables) {
      const { error } = await supabase
        .from(table)
        .delete()
        .in("round_id", removedIds);
      if (error) throw error;
    }

    const { error: roundsError } = await supabase
      .from("rounds")
      .delete()
      .in("id", removedIds);
    if (roundsError) throw roundsError;
  };

  const saveRounds = async (tournamentId) => {
    const payload = rounds.map((round, index) => ({
      ...(round.id ? { id: round.id } : {}),
      tournament_id: tournamentId,
      course_id: round.courseId || null,
      name: getRoundDisplayName(round, index),
      info: round.info || "",
      order_index: index,
      holes: round.holes === 9 ? 9 : 18,
    }));

    const { data, error } = await supabase
      .from("rounds")
      .upsert(payload, {
        onConflict: "id",
        defaultToNull: false,
      })
      .select();
    if (error) throw error;

    const savedRounds = [...(data || [])].sort(
      (first, second) => first.order_index - second.order_index
    );
    const roundIds = savedRounds.map((round) => round.id);

    if (roundIds.length > 0) {
      for (const table of [
        "scramble_custom_pairs",
        "scramble_settings",
        "round_modes",
      ]) {
        const { error: deleteError } = await supabase
          .from(table)
          .delete()
          .in("round_id", roundIds);
        if (deleteError) throw deleteError;
      }
    }

    const modePayload = [];
    const scramblePayload = [];
    const customPairPayload = [];

    savedRounds.forEach((savedRound, index) => {
      const configuredRound = rounds[index];
      (configuredRound.modes || [])
        .filter(Boolean)
        .forEach((mode, position) => {
          modePayload.push({
            round_id: savedRound.id,
            mode,
            position,
          });
        });

      const scramble = configuredRound.scrambleOptions;
      if (scramble) {
        scramblePayload.push({
          round_id: savedRound.id,
          scramble_with_handicap: Boolean(scramble.scrambleWithHandicap),
          pairing: scramble.pairing || "handicap",
          low_hcp: Number(scramble.lowPct) || 0,
          high_hcp: Number(scramble.highPct) || 0,
        });

        uniquePairsFromMap(scramble.customTeams).forEach(([playerOne, playerTwo]) => {
          customPairPayload.push({
            round_id: savedRound.id,
            player_1_id: playerOne,
            player_2_id: playerTwo,
          });
        });
      }
    });

    if (modePayload.length > 0) {
      const { error: modeError } = await supabase
        .from("round_modes")
        .insert(modePayload);
      if (modeError) throw modeError;
    }

    if (scramblePayload.length > 0) {
      const { error: scrambleError } = await supabase
        .from("scramble_settings")
        .insert(scramblePayload);
      if (scrambleError) throw scrambleError;
    }

    if (customPairPayload.length > 0) {
      const { error: pairError } = await supabase
        .from("scramble_custom_pairs")
        .insert(customPairPayload);
      if (pairError) throw pairError;
    }
  };

  const handleSaveTournament = async () => {
    clearMessages();

    if (validationIssues.length > 0) {
      setFormError(validationIssues[0]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (!user?.id) {
      setFormError("Du måste vara inloggad för att spara turneringen.");
      return;
    }

    setIsSaving(true);

    try {
      const tournamentId = await saveTournamentMetadata(
        editMode ? tournamentData?.id : null
      );

      if (editMode) {
        await clearRemovedRounds();
      }

      await syncTournamentPlayers(tournamentId);
      await saveMiniGames(tournamentId);
      await saveRounds(tournamentId);

      const updatedTournament = await rebuildTournamentData(
        tournamentId,
        selectedPlayerIds
      );

      setTournament?.(updatedTournament);

      if (editMode) {
        setActiveTab?.("standings");
        onSaved?.(updatedTournament);
      } else {
        navigate(`/turnering/${tournamentId}`);
      }
    } catch (error) {
      console.error("Kunde inte spara turneringen:", error);
      setFormError(
        error?.message
          ? `Turneringen kunde inte sparas: ${error.message}`
          : "Turneringen kunde inte sparas. Försök igen."
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSaving(false);
    }
  };

  function CustomPairPicker({ roundIndex, playerIds }) {
    const [selectedOne, setSelectedOne] = useState("");
    const [selectedTwo, setSelectedTwo] = useState("");

    const options = playerIds
      .map((playerId) =>
        players.find((player) => String(player.id) === String(playerId))
      )
      .filter(Boolean);

    const handleAdd = () => {
      addCustomPair(roundIndex, selectedOne, selectedTwo);
      setSelectedOne("");
      setSelectedTwo("");
    };

    return (
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <select
          value={selectedOne}
          onChange={(event) => setSelectedOne(event.target.value)}
          className="min-w-0 rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-sm text-[#1f2d26] outline-none focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
        >
          <option value="">Spelare 1</option>
          {options.map((player) => (
            <option key={player.id} value={player.id}>
              {player.name}
            </option>
          ))}
        </select>
        <select
          value={selectedTwo}
          onChange={(event) => setSelectedTwo(event.target.value)}
          className="min-w-0 rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-sm text-[#1f2d26] outline-none focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
        >
          <option value="">Spelare 2</option>
          {options.map((player) => (
            <option key={player.id} value={player.id}>
              {player.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!selectedOne || !selectedTwo || selectedOne === selectedTwo}
          onClick={handleAdd}
          className="rounded-xl bg-[#183d2d] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#102c20] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Lägg till par
        </button>
      </div>
    );
  }

  const renderCustomPairs = (round, roundIndex) => {
    const pairs = uniquePairsFromMap(round.scrambleOptions?.customTeams);
    const usedIds = new Set(pairs.flat());

    const renderGroup = (teamKey = null) => {
      const groupPlayerIds = selectedPlayerIds.filter((playerId) =>
        teamKey ? teamAssignments[playerId] === teamKey : true
      );
      const availableIds = groupPlayerIds.filter(
        (playerId) => !usedIds.has(String(playerId))
      );
      const groupPairs = pairs.filter(([first]) =>
        teamKey ? teamAssignments[first] === teamKey : true
      );
      const title = teamKey
        ? teamCaptains[teamKey] || (teamKey === "red" ? "Lag Röd" : "Lag Grön")
        : "Scramble-par";

      return (
        <div
          key={teamKey || "all"}
          className="rounded-2xl border border-[#d9e0dc] bg-white p-4"
        >
          <div className="flex items-center justify-between gap-3">
            <h5 className="text-sm font-semibold text-[#183d2d]">{title}</h5>
            <span className="text-[10px] font-medium text-[#79827d]">
              {groupPairs.length} par
            </span>
          </div>

          <div className="mt-3">
            {availableIds.length >= 2 ? (
              <CustomPairPicker roundIndex={roundIndex} playerIds={availableIds} />
            ) : (
              <p className="rounded-xl bg-[#f5f7f5] px-3 py-2.5 text-xs text-[#707a74]">
                {availableIds.length === 1
                  ? "En spelare återstår utan partner."
                  : "Alla tillgängliga spelare är parade."}
              </p>
            )}
          </div>

          {groupPairs.length > 0 && (
            <div className="mt-3 space-y-2">
              {groupPairs.map(([playerOne, playerTwo]) => {
                const firstName =
                  players.find((player) => String(player.id) === playerOne)?.name ||
                  "Spelare";
                const secondName =
                  players.find((player) => String(player.id) === playerTwo)?.name ||
                  "Spelare";

                return (
                  <div
                    key={`${playerOne}-${playerTwo}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[#e0e5e2] bg-[#fafbf9] px-3 py-2.5"
                  >
                    <span className="min-w-0 truncate text-sm font-medium text-[#2a3931]">
                      {firstName} <span className="text-[#a88035]">&amp;</span> {secondName}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeCustomPair(roundIndex, playerOne, playerTwo)}
                      className="shrink-0 rounded-lg p-1.5 text-[#9b4a47] transition hover:bg-[#f8eceb]"
                      title="Ta bort paret"
                    >
                      <XIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="grid gap-3 lg:grid-cols-2">
        {teamMode ? [renderGroup("red"), renderGroup("green")] : renderGroup()}
      </div>
    );
  };

  return (
    <div className={`${theme.page} ${theme.textPrimary} ${editMode ? "" : "min-h-screen"}`}>
      <div className="mx-auto max-w-7xl px-3 py-4 sm:px-5 sm:py-7 lg:px-7">
        <header className="relative overflow-hidden rounded-[26px] bg-[#183d2d] px-5 py-6 text-white shadow-[0_18px_50px_rgba(20,49,36,0.18)] sm:px-8 sm:py-8">
          <div className="absolute -right-16 -top-24 h-64 w-64 rounded-full border border-white/10" />
          <div className="absolute -right-6 -top-12 h-40 w-40 rounded-full border border-[#d0ac63]/25" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#d8bd83]">
                <TrophyIcon className="h-4 w-4" aria-hidden="true" />
                HoleInOne Tournament Studio
              </div>
              <h1 className="mt-3 text-3xl font-semibold tracking-[-0.035em] sm:text-4xl">
                {editMode ? "Redigera turneringen" : "Skapa en turnering"}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70 sm:text-base">
                Bygg ett tydligt och genomtänkt upplägg för spelare, rundor,
                lagtävling och sidotävlingar. Inställningarna används direkt i
                resultatrapportering och live-scorekort.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:min-w-[360px]">
              {[
                [selectedPlayerIds.length, "spelare"],
                [rounds.length, rounds.length === 1 ? "runda" : "rundor"],
                [miniGames.filter((game) => game.name.trim()).length, "sidotävlingar"],
              ].map(([value, label]) => (
                <div
                  key={label}
                  className="rounded-2xl border border-white/10 bg-white/[0.07] px-3 py-3 text-center backdrop-blur-sm"
                >
                  <p className="text-xl font-semibold tabular-nums">{value}</p>
                  <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.13em] text-white/50">
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </header>

        {(formError || formNotice) && (
          <div
            className={`mt-4 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
              formError
                ? "border-[#e7c5c2] bg-[#fbefee] text-[#873e3b]"
                : "border-[#cbdccf] bg-[#eef5f0] text-[#315b45]"
            }`}
          >
            {formError ? (
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            ) : (
              <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            )}
            <p className="leading-5">{formError || formNotice}</p>
            <button
              type="button"
              onClick={clearMessages}
              className="ml-auto shrink-0 rounded-lg p-1 opacity-60 transition hover:bg-black/5 hover:opacity-100"
              aria-label="Stäng meddelandet"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="mt-5 grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_310px]">
          <main className="space-y-5">
            <section className="rounded-[22px] border border-[#d9e0dc] bg-[#fffefa] p-4 shadow-[0_8px_26px_rgba(31,45,38,0.055)] sm:p-6">
              <SectionHeading
                number="1"
                icon={FlagIcon}
                title="Grunduppgifter"
                description="Ett tydligt namn gör turneringen lätt att hitta och dela."
              />
              <div className="mt-5">
                <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6d7771]">
                  Turneringsnamn
                </label>
                <input
                  type="text"
                  maxLength={80}
                  placeholder="Tävlingsnamn"
                  value={tournamentName}
                  onChange={(event) => {
                    setTournamentName(event.target.value);
                    setFormError("");
                  }}
                  className="w-full rounded-2xl border border-[#cdd7d1] bg-white px-4 py-3.5 text-base font-medium text-[#1f2d26] outline-none placeholder:text-[#a5ada8] focus:border-[#183d2d] focus:ring-4 focus:ring-[#183d2d]/[0.07]"
                />
                <div className="mt-2 flex items-center justify-between text-[10px] text-[#8a928e]">
                  <span>Visas som huvudrubrik på turneringssidan.</span>
                  <span className="tabular-nums">{tournamentName.length}/80</span>
                </div>
              </div>
            </section>

            <section className="rounded-[22px] border border-[#d9e0dc] bg-[#fffefa] p-4 shadow-[0_8px_26px_rgba(31,45,38,0.055)] sm:p-6">
              <SectionHeading
                number="2"
                icon={UsersIcon}
                title="Spelare"
                description="Välj deltagare och kontrollera att deras HCP är korrekt innan turneringen startar."
                right={
                  <span className="inline-flex w-fit items-center rounded-full bg-[#edf3ef] px-3 py-1.5 text-xs font-semibold text-[#183d2d]">
                    {selectedPlayerIds.length} valda
                  </span>
                }
              />

              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
                <label className="relative min-w-0 flex-1">
                  <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#87908b]" />
                  <input
                    type="search"
                    value={playerSearch}
                    onChange={(event) => setPlayerSearch(event.target.value)}
                    placeholder="Sök efter namn, klubb eller HCP"
                    className="w-full rounded-xl border border-[#d4dcd7] bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllFilteredPlayers}
                    disabled={filteredPlayers.length === 0}
                    className="flex-1 rounded-xl border border-[#d4dcd7] bg-white px-3 py-2.5 text-xs font-semibold text-[#4e5b54] transition hover:bg-[#f7f9f7] disabled:opacity-40 sm:flex-none"
                  >
                    Välj alla
                  </button>
                  <button
                    type="button"
                    onClick={clearSelectedPlayers}
                    disabled={selectedPlayerIds.length === 0}
                    className="flex-1 rounded-xl border border-[#d4dcd7] bg-white px-3 py-2.5 text-xs font-semibold text-[#7f4b48] transition hover:bg-[#fbf4f3] disabled:opacity-40 sm:flex-none"
                  >
                    Rensa
                  </button>
                </div>
              </div>

              <div className="mt-4">
                {playersLoading ? (
                  <div className="flex min-h-32 items-center justify-center rounded-2xl border border-dashed border-[#d4dcd7] bg-[#fafbf9] text-sm text-[#6e7872]">
                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                    Hämtar spelare…
                  </div>
                ) : filteredPlayers.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#d4dcd7] bg-[#fafbf9] px-4 py-8 text-center">
                    <UsersIcon className="mx-auto h-7 w-7 text-[#a0aaa4]" />
                    <p className="mt-2 text-sm font-medium text-[#4f5d55]">
                      {players.length === 0 ? "Inga spelare skapade ännu" : "Inga spelare matchar sökningen"}
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                    {filteredPlayers.map((player) => {
                      const playerId = String(player.id);
                      const selected = selectedPlayerIds.includes(playerId);
                      return (
                        <button
                          type="button"
                          key={player.id}
                          onClick={() => togglePlayer(playerId)}
                          className={`group flex min-w-0 items-center gap-3 rounded-2xl border p-3 text-left transition ${
                            selected
                              ? "border-[#183d2d] bg-[#edf3ef] shadow-[0_0_0_1px_rgba(24,61,45,0.08)]"
                              : "border-[#dce2de] bg-white hover:border-[#bdc9c1] hover:bg-[#fafcfb]"
                          }`}
                          aria-pressed={selected}
                        >
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                              selected
                                ? "border-[#183d2d] bg-[#183d2d] text-white"
                                : "border-[#bac5be] bg-white text-transparent"
                            }`}
                          >
                            <CheckCircle2Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-[#26352d]">
                              {player.name}
                            </span>
                            <span className="mt-0.5 block truncate text-[10px] text-[#7a837e]">
                              {player.home_club || player.club || "Klubb saknas"}
                            </span>
                          </span>
                          <span className="flex h-9 min-w-9 shrink-0 items-center justify-center rounded-full bg-[#f3ead8] px-2 text-[11px] font-bold text-[#866527]">
                            {formatHandicap(player.handicap)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {!showAddPlayer ? (
                <button
                  type="button"
                  onClick={() => setShowAddPlayer(true)}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#edf3ef] px-4 py-2.5 text-sm font-semibold text-[#183d2d] transition hover:bg-[#e3ece6]"
                >
                  <UserPlusIcon className="h-4 w-4" />
                  Skapa ny spelare
                </button>
              ) : (
                <div className="mt-4 rounded-2xl border border-[#d7dfda] bg-[#f7f9f7] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[#183d2d]">Ny spelare</p>
                      <p className="mt-0.5 text-xs text-[#707a74]">Spelaren väljs automatiskt efter att den sparats.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddPlayer(false)}
                      className="rounded-lg p-2 text-[#718079] hover:bg-black/5"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1.3fr)_100px_minmax(0,1fr)]">
                    <input
                      type="text"
                      placeholder="Namn"
                      value={newName}
                      onChange={(event) => setNewName(event.target.value)}
                      className="min-w-0 rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#183d2d]"
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="HCP"
                      value={newHandicap}
                      onChange={(event) => setNewHandicap(event.target.value)}
                      className="min-w-0 rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#183d2d]"
                    />
                    <input
                      type="text"
                      placeholder="Klubb (valfritt)"
                      value={newClub}
                      onChange={(event) => setNewClub(event.target.value)}
                      className="min-w-0 rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#183d2d]"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleQuickAddPlayer}
                    className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#183d2d] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#102c20]"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Spara spelare
                  </button>
                </div>
              )}
            </section>

            <section className="rounded-[22px] border border-[#d9e0dc] bg-[#fffefa] p-4 shadow-[0_8px_26px_rgba(31,45,38,0.055)] sm:p-6">
              <SectionHeading
                number="3"
                icon={ShieldCheckIcon}
                title="Stor lagtävling"
                description="Valfritt två-lagsläge som löper parallellt med den individuella tävlingen."
                right={
                  <button
                    type="button"
                    role="switch"
                    aria-checked={teamMode}
                    onClick={() => {
                      setTeamMode((current) => !current);
                      setFormError("");
                    }}
                    className={`relative h-7 w-12 shrink-0 rounded-full transition ${
                      teamMode ? "bg-[#183d2d]" : "bg-[#cbd3ce]"
                    }`}
                  >
                    <span
                      className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition ${
                        teamMode ? "left-6" : "left-1"
                      }`}
                    />
                  </button>
                }
              />

              {!teamMode ? (
                <div className="mt-5 rounded-2xl border border-dashed border-[#d3dbd6] bg-[#fafbf9] px-4 py-5 text-sm leading-6 text-[#6c7670]">
                  Aktivera läget för att skapa två huvudlag. Individuella resultat och
                  sidotävlings-avdrag kan då även räknas samman i lagställningen.
                </div>
              ) : (
                <div className="mt-5 space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="rounded-2xl border border-[#e0c7c4] bg-[#fbf3f2] p-4">
                      <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9f4a46]">Lag 1</span>
                      <input
                        type="text"
                        value={teamCaptains.red}
                        onChange={(event) =>
                          setTeamCaptains((current) => ({
                            ...current,
                            red: event.target.value,
                          }))
                        }
                        placeholder="Exempel: Team Faldo"
                        className="mt-2 w-full rounded-xl border border-[#dfc5c2] bg-white px-3 py-2.5 text-sm font-semibold text-[#3b2d2c] outline-none focus:border-[#9f3a38]"
                      />
                    </label>
                    <label className="rounded-2xl border border-[#c9ddd1] bg-[#f0f6f2] p-4">
                      <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#2f6b4f]">Lag 2</span>
                      <input
                        type="text"
                        value={teamCaptains.green}
                        onChange={(event) =>
                          setTeamCaptains((current) => ({
                            ...current,
                            green: event.target.value,
                          }))
                        }
                        placeholder="Exempel: Team Seve"
                        className="mt-2 w-full rounded-xl border border-[#c6d9cd] bg-white px-3 py-2.5 text-sm font-semibold text-[#25372d] outline-none focus:border-[#2f6b4f]"
                      />
                    </label>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs leading-5 text-[#717b75]">
                        Fördela samtliga valda spelare. Lagnycklarna är interna och påverkar inte lagnamnen.
                      </p>
                      <p className="mt-1 text-[10px] leading-4 text-[#89918d]">
                        Automatiken håller antalet spelare så jämnt som möjligt och testar kombinationerna för minsta möjliga skillnad i lagens sammanlagda HCP.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={autoBalanceTeams}
                      disabled={selectedPlayers.length < 2}
                      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-[#cfd8d2] bg-white px-3 py-2.5 text-xs font-semibold text-[#314239] transition hover:bg-[#f5f8f6] disabled:opacity-40"
                    >
                      <GaugeIcon className="h-4 w-4 text-[#a88035]" />
                      Fördela jämnt efter HCP
                    </button>
                  </div>

                  {teamBalanceOverview.red.count + teamBalanceOverview.green.count > 0 && (
                    <div className="grid grid-cols-2 gap-2 rounded-xl border border-[#dce2de] bg-[#f7f9f7] p-2.5 text-xs">
                      <div className="rounded-lg bg-white px-3 py-2">
                        <p className="truncate font-semibold text-[#9f4a46]">
                          {teamCaptains.red || "Lag 1"}
                        </p>
                        <p className="mt-0.5 text-[10px] text-[#78817c]">
                          {teamBalanceOverview.red.count} spelare · HCP {formatHandicap(teamBalanceOverview.red.handicap)}
                        </p>
                      </div>
                      <div className="rounded-lg bg-white px-3 py-2">
                        <p className="truncate font-semibold text-[#2f6b4f]">
                          {teamCaptains.green || "Lag 2"}
                        </p>
                        <p className="mt-0.5 text-[10px] text-[#78817c]">
                          {teamBalanceOverview.green.count} spelare · HCP {formatHandicap(teamBalanceOverview.green.handicap)}
                        </p>
                      </div>
                      <p className="col-span-2 px-1 text-center text-[10px] font-medium text-[#7f8983]">
                        Skillnad i sammanlagt HCP: {formatHandicap(teamBalanceOverview.difference)}
                      </p>
                    </div>
                  )}

                  {selectedPlayers.length === 0 ? (
                    <p className="rounded-xl bg-[#f6f7f6] px-4 py-3 text-sm text-[#747d78]">
                      Välj spelare ovan för att göra lagindelningen.
                    </p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {selectedPlayers.map((player) => {
                        const playerId = String(player.id);
                        return (
                          <div
                            key={player.id}
                            className="rounded-xl border border-[#dce2de] bg-white p-3"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="min-w-0 truncate text-sm font-semibold text-[#2a3931]">
                                {player.name}
                              </span>
                              <span className="text-[10px] font-medium text-[#89918d]">
                                HCP {formatHandicap(player.handicap)}
                              </span>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-1 rounded-lg bg-[#f4f6f4] p-1">
                              <SegmentedButton
                                active={teamAssignments[playerId] === "red"}
                                onClick={() => assignTeam(playerId, "red")}
                              >
                                {teamCaptains.red || "Lag 1"}
                              </SegmentedButton>
                              <SegmentedButton
                                active={teamAssignments[playerId] === "green"}
                                onClick={() => assignTeam(playerId, "green")}
                              >
                                {teamCaptains.green || "Lag 2"}
                              </SegmentedButton>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="rounded-[22px] border border-[#d9e0dc] bg-[#fffefa] p-4 shadow-[0_8px_26px_rgba(31,45,38,0.055)] sm:p-6">
              <SectionHeading
                number="4"
                icon={MedalIcon}
                title="Rundor och spelformer"
                description="Konfigurera varje runda. Individuellt slagspel kan spelas scratch eller HCP-justerat."
                right={
                  <div className="flex items-center rounded-xl border border-[#d5ddd8] bg-white p-1">
                    <button
                      type="button"
                      onClick={() => handleRoundsChange(rounds.length - 1)}
                      disabled={rounds.length <= 1}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-lg font-semibold text-[#5f6c65] hover:bg-[#f1f4f2] disabled:opacity-30"
                    >
                      −
                    </button>
                    <span className="min-w-9 text-center text-sm font-semibold tabular-nums text-[#183d2d]">
                      {rounds.length}
                    </span>
                    <button
                      type="button"
                      onClick={addRound}
                      disabled={rounds.length >= 12}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-lg font-semibold text-[#5f6c65] hover:bg-[#f1f4f2] disabled:opacity-30"
                    >
                      +
                    </button>
                  </div>
                }
              />

              <div className="mt-5 space-y-3">
                {rounds.map((round, index) => {
                  const mode = round.modes?.[0] || "";
                  const isOpen = openRoundIndexes.includes(index);
                  const isPreview = previewRoundIndexes.includes(index);
                  const showScrambleInfo = scrambleInfoIndexes.includes(index);
                  const isIndividualStrokePlay = [
                    "Slagspel",
                    "Handicap-justerat slagspel",
                  ].includes(mode);
                  const handicapPairGroups =
                    mode === "Scramble (2 man)" &&
                    round.scrambleOptions?.pairing === "handicap"
                      ? getScrambleHandicapPairGroups(round)
                      : [];
                  const selectedCourse = getCourseById(round.courseId);
                  const roundDisplayName = getRoundDisplayName(round, index);

                  return (
                    <article
                      key={round.id || `new-round-${index}`}
                      className="overflow-hidden rounded-[20px] border border-[#d8dfdb] bg-[#fafbf9]"
                    >
                      <div className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
                        <button
                          type="button"
                          onClick={() => toggleRoundOpen(index)}
                          className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#183d2d] text-sm font-semibold text-white">
                            {index + 1}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-[#203029] sm:text-base">
                              {roundDisplayName}
                            </span>
                            <span className="mt-0.5 block truncate text-[10px] text-[#7a837e]">
                              {mode || "Spelform saknas"} · {round.holes || 18} hål
                            </span>
                          </span>
                        </button>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => duplicateRound(index)}
                            className="rounded-lg p-2 text-[#6d7972] transition hover:bg-black/5"
                            title="Duplicera rundan"
                          >
                            <CopyIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeRound(index)}
                            disabled={rounds.length <= 1}
                            className="rounded-lg p-2 text-[#9b4a47] transition hover:bg-[#f8eceb] disabled:opacity-30"
                            title="Ta bort rundan"
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleRoundOpen(index)}
                            className="rounded-lg p-2 text-[#5f6b65] transition hover:bg-black/5"
                            aria-label={isOpen ? "Fäll ihop rundan" : "Expandera rundan"}
                          >
                            <ChevronDownIcon
                              className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                            />
                          </button>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="border-t border-[#dfe4e1] bg-white p-4 sm:p-5">
                          <CoursePicker
                            courses={courses}
                            loading={coursesLoading}
                            error={coursesError}
                            value={round.courseId}
                            onChange={(course) =>
                              updateRound(index, {
                                courseId: course?.id || null,
                              })
                            }
                          />

                          <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
                            <label>
                              <span className="mb-1.5 block text-[9px] font-semibold uppercase tracking-[0.15em] text-[#78817c]">
                                Rundnamn
                              </span>
                              <input
                                type="text"
                                value={round.name}
                                onChange={(event) =>
                                  updateRound(index, { name: event.target.value })
                                }
                                placeholder={
                                  selectedCourse?.name || `Runda ${index + 1}`
                                }
                                className="w-full rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-sm font-medium outline-none placeholder:text-[#9aa39e] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                              />
                              <p className="mt-1.5 text-[10px] leading-4 text-[#89918d]">
                                Lämna tomt för att använda{" "}
                                {selectedCourse
                                  ? "banans namn"
                                  : `Runda ${index + 1}`}.
                              </p>
                            </label>

                            <div>
                              <span className="mb-1.5 block text-[9px] font-semibold uppercase tracking-[0.15em] text-[#78817c]">
                                Antal hål
                              </span>
                              <div className="grid grid-cols-2 rounded-xl border border-[#d4dcd7] bg-[#f5f7f5] p-1">
                                {[9, 18].map((holes) => (
                                  <SegmentedButton
                                    key={holes}
                                    active={round.holes === holes}
                                    onClick={() => updateRound(index, { holes })}
                                  >
                                    {holes} hål
                                  </SegmentedButton>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="mt-5">
                            <div className="flex items-end justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-[#26362e]">Spelform</p>
                                <p className="mt-0.5 text-xs text-[#758079]">Välj hur resultatet ska räknas för rundan.</p>
                              </div>
                            </div>

                            <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                              <ChoiceCard
                                active={isIndividualStrokePlay}
                                title="Individuellt slagspel"
                                description="Scratch eller HCP-justerat slagspel."
                                icon={FlagIcon}
                                onClick={() =>
                                  selectRoundMode(
                                    index,
                                    isIndividualStrokePlay ? mode : "Slagspel"
                                  )
                                }
                              />
                              <ChoiceCard
                                active={mode === "Scramble (2 man)"}
                                title="2-manna scramble"
                                description="Två spelare delar resultat och spelar från valt läge."
                                icon={UsersIcon}
                                onClick={() => selectRoundMode(index, "Scramble (2 man)")}
                              />
                              <ChoiceCard
                                active={mode === "Bästboll"}
                                title="Bästboll"
                                description="Det bästa individuella resultatet räknas enligt upplägget."
                                icon={TrophyIcon}
                                onClick={() => selectRoundMode(index, "Bästboll")}
                              />
                              <ChoiceCard
                                active={mode === "Poängbogey"}
                                disabled
                                title="Poängbogey"
                                description="Kräver hålindex och banspecifik beräkning."
                                icon={TargetIcon}
                                badge="Kommer senare"
                              />
                            </div>

                            {isIndividualStrokePlay && (
                              <div className="mt-3 rounded-2xl border border-[#d7dfda] bg-[#f6f8f6] p-3 sm:flex sm:items-center sm:justify-between sm:gap-4">
                                <div>
                                  <p className="text-sm font-semibold text-[#26362e]">Resultatberäkning</p>
                                  <p className="mt-0.5 text-xs leading-5 text-[#737e77]">
                                    HCP-justerat sparar spelarens nettoresultat. På 9 hål används halvt HCP.
                                  </p>
                                </div>
                                <div className="mt-3 grid grid-cols-2 rounded-xl bg-white p-1 shadow-sm sm:mt-0 sm:min-w-[270px]">
                                  <SegmentedButton
                                    active={mode === "Slagspel"}
                                    onClick={() => selectRoundMode(index, "Slagspel")}
                                  >
                                    Scratch
                                  </SegmentedButton>
                                  <SegmentedButton
                                    active={mode === "Handicap-justerat slagspel"}
                                    onClick={() =>
                                      selectRoundMode(index, "Handicap-justerat slagspel")
                                    }
                                  >
                                    HCP-justerat
                                  </SegmentedButton>
                                </div>
                              </div>
                            )}
                          </div>

                          {mode === "Scramble (2 man)" && round.scrambleOptions && (
                            <div className="mt-5 rounded-[18px] border border-[#d8dfdb] bg-[#f7f9f7] p-4">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-[#183d2d]">Scramble-inställningar</p>
                                  <p className="mt-0.5 text-xs text-[#737d77]">Bestäm HCP-beräkning och hur paren skapas.</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => toggleScrambleInfo(index)}
                                  className="inline-flex w-fit items-center gap-1.5 text-xs font-semibold text-[#866527] hover:underline"
                                >
                                  <InfoIcon className="h-3.5 w-3.5" />
                                  {showScrambleInfo ? "Dölj förklaring" : "Förklara alternativen"}
                                </button>
                              </div>

                              {showScrambleInfo && (
                                <div className="mt-3 grid gap-2 rounded-xl border border-[#e3d7bd] bg-[#fffaf0] p-3 text-xs leading-5 text-[#6e5b34] sm:grid-cols-2">
                                  <p><strong>Scratch:</strong> lagets registrerade slag används utan HCP-avdrag.</p>
                                  <p><strong>HCP-justerat:</strong> låg och hög HCP viktas enligt procentsatserna.</p>
                                  <p><strong>Position:</strong> bäst placerad paras med sämst placerad inför rundan.</p>
                                  <p><strong>HCP-indelning:</strong> lägst HCP paras med högst, därefter näst lägst med näst högst.</p>
                                  <p><strong>Egen indelning:</strong> du väljer varje par manuellt.</p>
                                </div>
                              )}

                              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                                <div>
                                  <p className="mb-2 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#78817c]">Resultatberäkning</p>
                                  <div className="grid grid-cols-2 rounded-xl border border-[#d4dcd7] bg-white p-1">
                                    <SegmentedButton
                                      active={!round.scrambleOptions.scrambleWithHandicap}
                                      onClick={() =>
                                        updateScrambleOption(index, "scrambleWithHandicap", false)
                                      }
                                    >
                                      Scratch
                                    </SegmentedButton>
                                    <SegmentedButton
                                      active={Boolean(round.scrambleOptions.scrambleWithHandicap)}
                                      onClick={() =>
                                        updateScrambleOption(index, "scrambleWithHandicap", true)
                                      }
                                    >
                                      HCP-justerat
                                    </SegmentedButton>
                                  </div>

                                  {round.scrambleOptions.scrambleWithHandicap && (
                                    <div className="mt-3 grid grid-cols-2 gap-3">
                                      <label>
                                        <span className="mb-1 block text-[10px] font-medium text-[#68736c]">Låg HCP (%)</span>
                                        <input
                                          type="number"
                                          min="0"
                                          max="100"
                                          value={round.scrambleOptions.lowPct ?? ""}
                                          onChange={(event) =>
                                            updateScrambleOption(index, "lowPct", event.target.value)
                                          }
                                          className="w-full rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-center text-sm font-semibold outline-none focus:border-[#183d2d]"
                                        />
                                      </label>
                                      <label>
                                        <span className="mb-1 block text-[10px] font-medium text-[#68736c]">Hög HCP (%)</span>
                                        <input
                                          type="number"
                                          min="0"
                                          max="100"
                                          value={round.scrambleOptions.highPct ?? ""}
                                          onChange={(event) =>
                                            updateScrambleOption(index, "highPct", event.target.value)
                                          }
                                          className="w-full rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-center text-sm font-semibold outline-none focus:border-[#183d2d]"
                                        />
                                      </label>
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <p className="mb-2 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#78817c]">Lagindelning</p>
                                  <div className="grid grid-cols-3 rounded-xl border border-[#d4dcd7] bg-white p-1">
                                    <SegmentedButton
                                      active={round.scrambleOptions.pairing === "position"}
                                      disabled={index === 0}
                                      onClick={() => updateScrambleOption(index, "pairing", "position")}
                                    >
                                      Position
                                    </SegmentedButton>
                                    <SegmentedButton
                                      active={round.scrambleOptions.pairing === "handicap"}
                                      onClick={() => updateScrambleOption(index, "pairing", "handicap")}
                                    >
                                      HCP
                                    </SegmentedButton>
                                    <SegmentedButton
                                      active={round.scrambleOptions.pairing === "custom"}
                                      onClick={() => updateScrambleOption(index, "pairing", "custom")}
                                    >
                                      Egen
                                    </SegmentedButton>
                                  </div>
                                  {index === 0 && (
                                    <p className="mt-1.5 text-[10px] leading-4 text-[#89918d]">Position blir tillgängligt från runda 2.</p>
                                  )}
                                </div>
                              </div>

                              {round.scrambleOptions.pairing === "handicap" && (
                                <div className="mt-4 rounded-xl border border-[#d6ded9] bg-white p-3.5">
                                  <div className="flex items-start gap-2.5">
                                    <GaugeIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#a88035]" />
                                    <div>
                                      <p className="text-xs font-semibold text-[#26362e]">
                                        Så skapas jämna scramblelag
                                      </p>
                                      <p className="mt-1 text-[11px] leading-5 text-[#6f7a73]">
                                        Lägst HCP paras med högst, därefter näst lägst med näst högst. På så sätt blir lagens sammanlagda HCP så jämnt som möjligt. Är den stora lagtävlingen aktiverad görs parningen separat inom respektive huvudlag.
                                      </p>
                                    </div>
                                  </div>

                                  {selectedPlayers.length >= 2 && (
                                    <div className="mt-3 space-y-3 border-t border-[#e2e7e4] pt-3">
                                      {handicapPairGroups.map((group) => (
                                        <div key={group.key}>
                                          {teamMode && (
                                            <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-[#7d8781]">
                                              {group.label}
                                            </p>
                                          )}
                                          <div className="grid gap-1.5 sm:grid-cols-2">
                                            {group.pairs.map((pair) => (
                                              <div
                                                key={`${group.key}-${pair.players[0].id}-${pair.players[1].id}`}
                                                className="flex items-center justify-between gap-2 rounded-lg bg-[#f6f8f6] px-3 py-2"
                                              >
                                                <span className="min-w-0 truncate text-[11px] font-medium text-[#34443b]">
                                                  {pair.players[0].name} + {pair.players[1].name}
                                                </span>
                                                <span className="shrink-0 text-[9px] font-semibold tabular-nums text-[#7b8580]">
                                                  Σ HCP {formatHandicap(pair.combinedHandicap)}
                                                </span>
                                              </div>
                                            ))}
                                            {group.singlePlayer && (
                                              <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-[#dfd3b9] bg-[#fffaf0] px-3 py-2">
                                                <span className="min-w-0 truncate text-[11px] font-medium text-[#6e5b34]">
                                                  {group.singlePlayer.name}
                                                </span>
                                                <span className="shrink-0 text-[9px] font-semibold text-[#927431]">
                                                  Ensam spelare
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {round.scrambleOptions.pairing === "custom" && (
                                <div className="mt-4 border-t border-[#dce2de] pt-4">
                                  <div className="mb-3">
                                    <p className="text-sm font-semibold text-[#26362e]">Egen lagindelning</p>
                                    <p className="mt-0.5 text-xs text-[#747e78]">Spelare som redan ingår i ett par tas bort från valen.</p>
                                  </div>
                                  {renderCustomPairs(round, index)}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="mt-5 rounded-[18px] border border-[#d8dfdb] bg-[#fafbf9] p-4">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <p className="text-sm font-semibold text-[#26362e]">Rundinformation</p>
                                <p className="mt-0.5 text-xs text-[#747e78]">Tider, regler och praktisk information som visas för deltagarna.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => toggleRoundPreview(index)}
                                className="inline-flex w-fit items-center gap-2 rounded-xl border border-[#d4dcd7] bg-white px-3 py-2 text-xs font-semibold text-[#546159] hover:bg-[#f5f7f5]"
                              >
                                <ClipboardCheckIcon className="h-4 w-4" />
                                {isPreview ? "Redigera text" : "Förhandsgranska"}
                              </button>
                            </div>

                            {isPreview ? (
                              <div className="mt-3 min-h-36 rounded-xl border border-[#e0d3b8] bg-[#fffaf0] p-4 text-sm leading-6 text-[#3d453f]">
                                {round.info.trim() ? (
                                  <div dangerouslySetInnerHTML={{ __html: safeInfoHtml(round.info) }} />
                                ) : (
                                  <p className="text-[#8c948f]">Ingen rundinformation är angiven.</p>
                                )}
                              </div>
                            ) : (
                              <>
                                <textarea
                                  rows={6}
                                  value={round.info}
                                  onChange={(event) => updateRound(index, { info: event.target.value })}
                                  placeholder="Tee time, samling, regler och övrig information…"
                                  className="mt-3 w-full resize-y rounded-xl border border-[#ccd6d0] bg-white px-3 py-3 text-sm leading-6 outline-none placeholder:text-[#a2aaa5] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                                />
                                <p className="mt-1.5 text-[10px] text-[#89918d]">Använd &lt;strong&gt;text&lt;/strong&gt; för fetstil.</p>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={addRound}
                disabled={rounds.length >= 12}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#bfcac3] bg-[#fafbf9] px-4 py-3 text-sm font-semibold text-[#425149] transition hover:border-[#183d2d] hover:bg-[#f2f6f3] disabled:opacity-40"
              >
                <PlusIcon className="h-4 w-4" />
                Lägg till runda
              </button>
            </section>

            <section className="rounded-[22px] border border-[#d9e0dc] bg-[#fffefa] p-4 shadow-[0_8px_26px_rgba(31,45,38,0.055)] sm:p-6">
              <SectionHeading
                number="5"
                icon={TargetIcon}
                title="Sidotävlingar"
                description="Lägg till sidotävlingar och välj om vinster ska ge slagavdrag i individ- och lagtävlingen."
                right={
                  <span className="inline-flex w-fit items-center rounded-full bg-[#f3ead8] px-3 py-1.5 text-xs font-semibold text-[#826225]">
                    {miniGames.length} tillagda
                  </span>
                }
              />

              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {MINI_GAME_PRESETS.map((preset) => {
                  const added = miniGames.some(
                    (game) => game.gameType === preset.gameType
                  );
                  return (
                    <button
                      type="button"
                      key={preset.gameType}
                      onClick={() => addMiniGamePreset(preset)}
                      disabled={added}
                      className={`rounded-2xl border p-4 text-left transition ${
                        added
                          ? "border-[#cbdccf] bg-[#eef5f0]"
                          : "border-[#dce2de] bg-white hover:border-[#b8c6bd] hover:bg-[#fafcfb]"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#f3ead8] text-[#896827]">
                          <TargetIcon className="h-4 w-4" />
                        </span>
                        {added ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#326047]">
                            <CheckCircle2Icon className="h-3.5 w-3.5" /> Tillagd
                          </span>
                        ) : (
                          <PlusIcon className="h-4 w-4 text-[#7d8881]" />
                        )}
                      </div>
                      <p className="mt-3 text-sm font-semibold text-[#26362e]">{preset.name}</p>
                      <p className="mt-1 text-xs leading-5 text-[#747e78]">{preset.description}</p>
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={addCustomMiniGame}
                  className="rounded-2xl border border-dashed border-[#c8d1cc] bg-[#fafbf9] p-4 text-left transition hover:border-[#183d2d] hover:bg-[#f3f7f4]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#edf3ef] text-[#183d2d]">
                    <SparklesIcon className="h-4 w-4" />
                  </span>
                  <p className="mt-3 text-sm font-semibold text-[#26362e]">Egen sidotävling</p>
                  <p className="mt-1 text-xs leading-5 text-[#747e78]">Skapa en valfri sidotävling som registreras manuellt.</p>
                </button>
              </div>

              {miniGames.length > 0 && (
                <div className="mt-5 space-y-3">
                  {miniGames.map((game, index) => (
                    <article
                      key={game.id || `new-mini-game-${index}`}
                      className="rounded-[18px] border border-[#d9e0dc] bg-[#fafbf9] p-4"
                    >
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#183d2d] text-white">
                          <TargetIcon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="rounded-full bg-[#f3ead8] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-[#866527]">
                              {getMiniGameTypeLabel(game.gameType)}
                            </span>
                          </div>
                          <input
                            type="text"
                            value={game.name}
                            onChange={(event) =>
                              updateMiniGame(index, { name: event.target.value })
                            }
                            placeholder={`Sidotävling ${index + 1}`}
                            className="mt-2 w-full rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 text-sm font-semibold text-[#26362e] outline-none focus:border-[#183d2d]"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeMiniGame(index)}
                          className="shrink-0 rounded-lg p-2 text-[#9b4a47] hover:bg-[#f8eceb]"
                          title="Ta bort sidotävling"
                        >
                          <Trash2Icon className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_150px] sm:items-end">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={game.subtract}
                          onClick={() =>
                            updateMiniGame(index, {
                              subtract: !game.subtract,
                              deduction: !game.subtract && !(Number(game.deduction) > 0) ? 3 : game.deduction,
                            })
                          }
                          className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                            game.subtract
                              ? "border-[#c9d9ce] bg-[#eef5f0]"
                              : "border-[#d9e0dc] bg-white"
                          }`}
                        >
                          <span
                            className={`relative h-6 w-11 shrink-0 rounded-full transition ${
                              game.subtract ? "bg-[#183d2d]" : "bg-[#cbd3ce]"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition ${
                                game.subtract ? "left-[22px]" : "left-0.5"
                              }`}
                            />
                          </span>
                          <span>
                            <span className="block text-xs font-semibold text-[#2c3b33]">Ger slagavdrag</span>
                            <span className="mt-0.5 block text-[10px] leading-4 text-[#78827c]">Varje vinst dras från totalresultatet.</span>
                          </span>
                        </button>

                        <label className={game.subtract ? "block" : "pointer-events-none opacity-35"}>
                          <span className="mb-1.5 block text-[9px] font-semibold uppercase tracking-[0.14em] text-[#78817c]">Avdrag per vinst</span>
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              value={game.deduction}
                              disabled={!game.subtract}
                              onChange={(event) =>
                                updateMiniGame(index, {
                                  deduction: Number(event.target.value),
                                })
                              }
                              className="w-full rounded-xl border border-[#ccd6d0] bg-white px-3 py-2.5 pr-12 text-center text-sm font-semibold outline-none focus:border-[#183d2d]"
                            />
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-[#7d8781]">slag</span>
                          </div>
                        </label>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </main>

          <aside className="lg:sticky lg:top-5">
            <div className="overflow-hidden rounded-[22px] border border-[#d5ddd8] bg-[#fffefa] shadow-[0_12px_36px_rgba(31,45,38,0.08)]">
              <div className="bg-[#183d2d] px-5 py-5 text-white">
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#d8bd83]">
                  <ClipboardCheckIcon className="h-4 w-4" />
                  Sammanfattning
                </div>
                <h3 className="mt-2 truncate text-lg font-semibold">
                  {tournamentName.trim() || "Namnlös turnering"}
                </h3>
                <p className="mt-1 text-xs leading-5 text-white/55">
                  Kontrollera upplägget innan du sparar.
                </p>
              </div>

              <div className="p-5">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-[#f2f5f3] px-3 py-3">
                    <p className="text-xl font-semibold tabular-nums text-[#183d2d]">{selectedPlayerIds.length}</p>
                    <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-[#7b857f]">Spelare</p>
                  </div>
                  <div className="rounded-xl bg-[#f2f5f3] px-3 py-3">
                    <p className="text-xl font-semibold tabular-nums text-[#183d2d]">{rounds.length}</p>
                    <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-[#7b857f]">Rundor</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {rounds.map((round, index) => (
                    <div
                      key={round.id || index}
                      className="flex items-center gap-3 rounded-xl border border-[#e0e5e2] bg-white px-3 py-2.5"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#edf3ef] text-[10px] font-bold text-[#183d2d]">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-semibold text-[#2c3a33]">{getRoundDisplayName(round, index)}</span>
                        <span className="mt-0.5 block truncate text-[9px] text-[#858e89]">{round.modes?.[0] || "Spelform saknas"} · {round.holes} hål</span>
                      </span>
                    </div>
                  ))}
                </div>

                {teamMode && (
                  <div className="mt-4 rounded-xl border border-[#d7dfda] bg-[#f8faf8] px-3 py-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#35443c]">
                      <ShieldCheckIcon className="h-4 w-4 text-[#a88035]" />
                      Stor lagtävling aktiverad
                    </div>
                    <p className="mt-1 text-[10px] leading-4 text-[#7a837e]">{teamCaptains.red || "Lag 1"} mot {teamCaptains.green || "Lag 2"}</p>
                  </div>
                )}

                <div className="mt-5 border-t border-[#e0e5e2] pt-4">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#7b857f]">Kontroll</p>
                  <div className="mt-2 space-y-2">
                    {validationIssues.length === 0 ? (
                      <div className="flex items-start gap-2 rounded-xl bg-[#eef5f0] px-3 py-2.5 text-xs leading-5 text-[#315b45]">
                        <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" />
                        Alla obligatoriska uppgifter är ifyllda.
                      </div>
                    ) : (
                      validationIssues.slice(0, 4).map((issue) => (
                        <div key={issue} className="flex items-start gap-2 text-[11px] leading-4 text-[#89504c]">
                          <AlertCircleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
                          {issue}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {advisoryItems.length > 0 && (
                  <div className="mt-4 rounded-xl border border-[#e7dcc4] bg-[#fffaf0] px-3 py-3">
                    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#866527]">
                      <InfoIcon className="h-3.5 w-3.5" /> Att tänka på
                    </p>
                    <div className="mt-2 space-y-1.5">
                      {advisoryItems.slice(0, 3).map((item) => (
                        <p key={item} className="text-[10px] leading-4 text-[#716344]">• {item}</p>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleSaveTournament}
                  disabled={isSaving || validationIssues.length > 0}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#183d2d] px-4 py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(24,61,45,0.2)] transition hover:bg-[#102c20] disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {isSaving ? (
                    <>
                      <Loader2Icon className="h-4 w-4 animate-spin" />
                      Sparar…
                    </>
                  ) : (
                    <>
                      <SaveIcon className="h-4 w-4" />
                      {editMode ? "Spara ändringar" : "Skapa turnering"}
                    </>
                  )}
                </button>

                <p className="mt-2 text-center text-[9px] leading-4 text-[#929a96]">
                  Poängbogey är synligt som kommande spelform men kan ännu inte väljas.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}